import React, { useState, useEffect } from 'react';
import { useSettings } from '@/contexts/SettingsContext';
import { useCategories } from '@/contexts/CategoriesContext';
import { useTranslation } from '@/hooks/useTranslation';
import type {
  RecurringTemplate,
  RecurringTemplateInput,
  RecurringFrequency,
} from '@/types/recurring';
import { X } from 'lucide-react';

interface RecurringTransactionFormProps {
  template?: RecurringTemplate | null;
  onSubmit: (data: RecurringTemplateInput) => Promise<void>;
  onCancel: () => void;
}

export function RecurringTransactionForm({
  template,
  onSubmit,
  onCancel,
}: RecurringTransactionFormProps) {
  const { settings } = useSettings();
  const language = settings?.language || 'en';
  const { gainCategories, expenseCategories, getCategoryLabel: getCtxCategoryLabel } = useCategories();
  const { t, td, tm } = useTranslation();

  const [formData, setFormData] = useState<RecurringTemplateInput>({
    name: '',
    amount: 0,
    type: 'expense',
    categoryId: '',
    frequency: 'monthly',
    startDate: new Date(),
    endDate: undefined,
    dayOfWeek: undefined,
    dayOfMonth: undefined,
    monthOfYear: undefined,
  });

  const [submitting, setSubmitting] = useState(false);

  const parseDateLocal = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const toLocalDateString = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        amount: template.amount,
        type: template.type,
        categoryId: template.categoryId,
        frequency: template.frequency,
        startDate: template.startDate,
        endDate: template.endDate,
        dayOfWeek: template.dayOfWeek,
        dayOfMonth: template.dayOfMonth,
        monthOfYear: template.monthOfYear,
      });
    }
  }, [template]);

  useEffect(() => {
    const updates: Partial<RecurringTemplateInput> = {};
    const today = new Date();

    switch (formData.frequency) {
      case 'daily':
        updates.dayOfWeek = undefined;
        updates.dayOfMonth = undefined;
        updates.monthOfYear = undefined;
        break;
      case 'weekly':
        if (formData.dayOfWeek === undefined) {
          updates.dayOfWeek = today.getDay();
        }
        updates.dayOfMonth = undefined;
        updates.monthOfYear = undefined;
        break;
      case 'monthly':
        updates.dayOfWeek = undefined;
        if (formData.dayOfMonth === undefined) {
          updates.dayOfMonth = today.getDate();
        }
        updates.monthOfYear = undefined;
        break;
      case 'yearly':
        updates.dayOfWeek = undefined;
        if (formData.dayOfMonth === undefined) {
          updates.dayOfMonth = today.getDate();
        }
        if (formData.monthOfYear === undefined) {
          updates.monthOfYear = today.getMonth() + 1;
        }
        break;
    }

    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
    }
  }, [formData.frequency, formData.dayOfWeek, formData.dayOfMonth, formData.monthOfYear]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert(t('errors.enterName'));
      return;
    }

    if (formData.amount <= 0) {
      alert(t('errors.enterAmount'));
      return;
    }

    if (!formData.categoryId) {
      alert(t('errors.selectCategory'));
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(t('errors.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const categories = formData.type === 'gain' ? gainCategories : expenseCategories;

  // Localised day & month arrays via translation hook
  const dayOptions = [0, 1, 2, 3, 4, 5, 6].map((i) => ({ value: i, label: td(i) }));
  const monthOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => ({
    value: i + 1,
    label: tm(i),
  }));

  const inputClass =
    'w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-700 dark:text-slate-100';
  const labelClass = 'mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-2xl dark:bg-slate-800 sm:max-w-lg sm:rounded-2xl">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 sm:text-xl">
            {template ? t('recurring.editRecurring') : t('recurring.title')}
          </h2>
          <button
            onClick={onCancel}
            className="rounded-lg p-2 text-slate-600 transition-all hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-5 sm:px-6">
          <div className="space-y-5">
            {/* Type Selection */}
            <div>
              <label className={labelClass}>
                {language === 'fr' ? 'Type' : 'Type'}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, type: 'gain', categoryId: '' }))
                  }
                  className={`rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                    formData.type === 'gain'
                      ? 'bg-emerald-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                  }`}
                >
                  💰 {language === 'fr' ? 'Revenu' : 'Income'}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, type: 'expense', categoryId: '' }))
                  }
                  className={`rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                    formData.type === 'expense'
                      ? 'bg-red-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                  }`}
                >
                  💳 {language === 'fr' ? 'Dépense' : 'Expense'}
                </button>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className={labelClass}>
                {language === 'fr' ? 'Nom' : 'Name'}
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder={language === 'fr' ? 'ex. Loyer, Salaire, Netflix' : 'e.g., Rent, Salary, Netflix'}
                className={inputClass}
                required
              />
            </div>

            {/* Amount */}
            <div>
              <label className={labelClass}>
                {language === 'fr' ? 'Montant' : 'Amount'}
              </label>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={formData.amount || ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    amount: parseFloat(e.target.value) || 0,
                  }))
                }
                placeholder="0.00"
                className={inputClass}
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className={labelClass}>
                {language === 'fr' ? 'Catégorie' : 'Category'}
              </label>
              <select
                value={formData.categoryId}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, categoryId: e.target.value }))
                }
                className={inputClass}
                required
              >
                <option value="">
                  {language === 'fr' ? 'Sélectionner une catégorie...' : 'Select category...'}
                </option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Frequency */}
            <div>
              <label className={labelClass}>{t('recurring.frequency')}</label>
              <select
                value={formData.frequency}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    frequency: e.target.value as RecurringFrequency,
                  }))
                }
                className={inputClass}
              >
                <option value="daily">{t('recurring.daily')}</option>
                <option value="weekly">{t('recurring.weekly')}</option>
                <option value="monthly">{t('recurring.monthly')}</option>
                <option value="yearly">{t('recurring.yearly')}</option>
              </select>
            </div>

            {/* Weekly: Day of week */}
            {formData.frequency === 'weekly' && (
              <div>
                <label className={labelClass}>{t('recurring.dayOfWeek')}</label>
                <select
                  value={formData.dayOfWeek ?? 0}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      dayOfWeek: parseInt(e.target.value, 10),
                    }))
                  }
                  className={inputClass}
                >
                  {dayOptions.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Monthly: Day of month */}
            {formData.frequency === 'monthly' && (
              <div>
                <label className={labelClass}>{t('recurring.dayOfMonth')}</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max="31"
                  value={formData.dayOfMonth ?? 1}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      dayOfMonth: parseInt(e.target.value, 10),
                    }))
                  }
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-slate-500">
                  {language === 'fr'
                    ? "Si le jour n'existe pas dans un mois (ex. le 31 en février), le dernier jour du mois sera utilisé."
                    : "If the day doesn't exist in a month (e.g., 31st in February), it will use the last day of that month."}
                </p>
              </div>
            )}

            {/* Yearly: Month + Day */}
            {formData.frequency === 'yearly' && (
              <>
                <div>
                  <label className={labelClass}>{t('recurring.monthOfYear')}</label>
                  <select
                    value={formData.monthOfYear ?? 1}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        monthOfYear: parseInt(e.target.value, 10),
                      }))
                    }
                    className={inputClass}
                  >
                    {monthOptions.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t('recurring.dayOfMonth')}</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="31"
                    value={formData.dayOfMonth ?? 1}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dayOfMonth: parseInt(e.target.value, 10),
                      }))
                    }
                    className={inputClass}
                  />
                </div>
              </>
            )}

            {/* Start Date */}
            <div>
              <label className={labelClass}>{t('recurring.startDate')}</label>
              <input
                type="date"
                value={toLocalDateString(formData.startDate)}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    startDate: parseDateLocal(e.target.value),
                  }))
                }
                className={inputClass}
                required
              />
            </div>

            {/* End Date (Optional) */}
            <div>
              <label className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.endDate !== undefined}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      endDate: e.target.checked ? new Date() : undefined,
                    }))
                  }
                  className="rounded"
                />
                {t('recurring.setEndDate')}
              </label>
              {formData.endDate !== undefined && (
                <input
                  type="date"
                  value={toLocalDateString(formData.endDate)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      endDate: parseDateLocal(e.target.value),
                    }))
                  }
                  min={toLocalDateString(formData.startDate)}
                  className={inputClass}
                />
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex gap-3 pb-safe">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded-xl bg-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition-all hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-blue-700 hover:to-purple-700 disabled:opacity-50"
            >
              {submitting
                ? t('common.saving') || (language === 'fr' ? 'Enregistrement...' : 'Saving...')
                : template
                ? t('common.save')
                : language === 'fr' ? 'Créer' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
